import {useAuth} from '@apps-common/auth-react';
import MosaicsIcon from '@apps-common/ui-icons/Mosaics.jsx';
import DestinationsIcon from '@mui/icons-material/Hub';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import AreasOfInterestIcon from '@mui/icons-material/LocationOnOutlined';
import SubscriptionsIcon from '@mui/icons-material/Mediation';
import EmptyStateIcon from '@mui/icons-material/Public';
import DataCollectionsIcon from '@mui/icons-material/Storage';
import OrdersIcon from '@mui/icons-material/TextSnippetOutlined';
import SearchAndOrderIcon from '@mui/icons-material/TravelExplore';
import {
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import {useCallback, useState} from 'react';
import {Link as RouteLink} from 'react-router';
import {useDataProducts} from '@api/products/useDataProducts';
import PageWrapper from '@components/common/PageWrapper.jsx';
import {EVENT_ACTIONS} from '@src/analytics.js';
import EmptyMessage from '@src/components/common/EmptyMessage.tsx';
import RotatingLoadingMessage from '@src/components/common/RotatingLoadingMessage.tsx';
import {
  DATA_COLLECTIONS_URL,
  EXPLORER_URL,
  FEATURES_MANAGER_URL,
} from '@src/config';
import {useFlags} from '@src/hooks/useFlags';
import {useTrackSectionEvent} from '@src/hooks/useTrackSectionEvent.js';
import {formatDateISO} from '@src/utils/dates';

const NUM_DATA_PRODUCTS_TO_SHOW = 15;

const LOADING_MESSAGES = [
  'Powering up a space mission 🚀',
  'Pulling from the far reaches of the galaxy 🪐',
  'Consulting ancient star maps 💫',
  'Scanning for life forms 🛰️',
  'Running systems check on the mothership 🤖',
  'Synchronizing with planetary orbits 🪐',
  'Collecting cosmic dust ✨',
];

const Section = ({title, children, description, testId}) => (
  <Stack data-testid={testId} sx={{maxWidth: '400px', gap: 2}}>
    <Stack sx={{gap: 1}}>
      <Typography sx={{color: 'text.secondary'}} variant="subtitle1">
        {title}
      </Typography>

      <Typography variant="body2">{description}</Typography>
    </Stack>

    <Paper elevation={1}>
      <List disablePadding>{children}</List>
    </Paper>
  </Stack>
);

Section.propTypes = {
  children: PropTypes.any,
  description: PropTypes.string,
  testId: PropTypes.string,
  title: PropTypes.string,
};

const Resource = ({
  description,
  href,
  icon,
  onClick,
  subheader,
  title,
  ...props
}) => (
  <ListItem
    disablePadding
    onClick={onClick}
    sx={{
      '&.MuiListItem-root:not(:first-of-type)': {
        borderTop: 'solid 1px',
        borderTopColor: 'divider',
      },
      '& .MuiListItemButton-root': {
        gap: 2.5,
        paddingLeft: 2.5,
      },
      display: 'block',
    }}
  >
    <ListItemButton
      onClick={onClick}
      to={href}
      sx={{
        minHeight: 48,
        justifyContent: open ? 'initial' : 'center',
        gap: open ? 2 : 0,
        paddingX: 1.5,
        borderRadius: 1,
      }}
      {...props}
    >
      {icon && (
        <ListItemIcon
          sx={{
            minWidth: 0,
            justifyContent: 'center',
            color: 'action.active',
          }}
        >
          {icon}
        </ListItemIcon>
      )}

      <ListItemText
        sx={{
          opacity: open ? 1 : 0,
        }}
        primary={
          <Stack sx={{display: 'inline-flex', gap: 0}}>
            <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
              <Typography variant="subtitle1">{title}</Typography>

              {description && (
                <Tooltip title={description}>
                  <InfoIcon sx={{color: 'text.secondary', fontSize: '18px'}} />
                </Tooltip>
              )}
            </Stack>

            {subheader && (
              <Typography sx={{color: 'text.secondary'}} variant="caption">
                {subheader}
              </Typography>
            )}
          </Stack>
        }
      />
    </ListItemButton>
  </ListItem>
);

Resource.propTypes = {
  description: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  subheader: PropTypes.string,
  title: PropTypes.string,
};

const ProductCard = ({title, tooltipContent, startDate, endDate, visible}) => {
  return (
    <Paper
      aria-label={title}
      role="listitem"
      elevation={1}
      sx={{
        border: 'solid 1px',
        borderColor: 'divider',
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        gap: 0.5,
        paddingBlockStart: 0.75,
        paddingBlockEnd: 0.75,
        paddingInlineStart: 1.25,
        paddingInlineEnd: 1,
        width: 250,
      }}
    >
      <Stack
        direction="row"
        sx={{alignItems: 'top', justifyContent: 'space-between', gap: 1}}
      >
        <Typography
          component="span"
          sx={{wordBreak: 'break-word'}}
          variant="body1"
        >
          {title}
        </Typography>

        {tooltipContent && (
          <Tooltip title={tooltipContent}>
            <InfoIcon sx={{color: 'text.secondary', fontSize: '18px'}} />
          </Tooltip>
        )}
      </Stack>

      {(startDate || endDate) && (
        <Typography variant="caption" sx={{color: 'text.secondary'}}>
          {formatDateISO(startDate) || '--'} to {formatDateISO(endDate) || '--'}
        </Typography>
      )}
    </Paper>
  );
};

ProductCard.propTypes = {
  title: PropTypes.string,
  tooltipContent: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  visible: PropTypes.bool,
};

export const Overview = () => {
  const trackSectionEvent = useTrackSectionEvent();
  const {showCreateSubscription} = useFlags();
  const [showAllProducts, setShowAllProducts] = useState(false);
  const {user} = useAuth();
  const orgId = user?.claims?.organization_id;

  const mdDown = useMediaQuery(theme => theme.breakpoints.down('md'));

  const {products, isLoading, error} = useDataProducts({
    orgId,
  });

  const showDataProducts = !isLoading && !error;

  const trackNavEvent = useCallback(
    label => trackSectionEvent(EVENT_ACTIONS.NAVIGATE_TO, label),
    [trackSectionEvent],
  );

  return (
    <PageWrapper maxWidth="initial">
      <Stack direction={{sm: 'column', md: 'row'}} sx={{gap: mdDown ? 4 : 8}}>
        <Stack sx={{gap: 1, order: mdDown ? 3 : 1}}>
          <Typography variant="overline2">Data workflows</Typography>

          <Stack sx={{gap: 4}}>
            <Section
              title="Add data"
              description="After you add AOIs, create subscriptions for data products or browse and order select imagery."
              testId="add-data-section"
            >
              <Resource
                title="Search and order"
                description="Search for, preview, and order specific images needed for your applications. Best for finding and ordering discrete images within Planet’s archive."
                href={EXPLORER_URL}
                icon={<SearchAndOrderIcon />}
                onClick={() => trackNavEvent('Explorer')}
                subheader="Orders API"
              />

              {showCreateSubscription && (
                <Resource
                  title="Subscribe to data"
                  description="Deliver all images within an area and time of interest that meet your criteria. Best for requesting a timestack of data or to create recurring deliveries to your data collections."
                  href="/subscriptions/new"
                  component={RouteLink}
                  icon={<SubscriptionsIcon />}
                  onClick={() => trackNavEvent('Subscribe to data')}
                  subheader="Subscriptions API"
                />
              )}

              <Resource
                title="Areas of interest"
                href={FEATURES_MANAGER_URL}
                icon={<AreasOfInterestIcon />}
                onClick={() => trackNavEvent('Features Manager')}
              />
            </Section>

            <Section
              title="View data delivery"
              description="Manage your subscriptions and orders. Kick off analysis in data collections."
              testId="view-data-section"
            >
              <Resource
                title="Orders"
                href="/orders"
                icon={<OrdersIcon />}
                component={RouteLink}
                onClick={() => trackNavEvent('Orders')}
                subheader="Orders API"
              />

              <Resource
                title="Subscriptions"
                href="/subscriptions"
                icon={<SubscriptionsIcon />}
                component={RouteLink}
                onClick={() => trackNavEvent('Subscriptions')}
                subheader="Subscriptions API"
              />

              <Resource
                title="Data collections"
                href={DATA_COLLECTIONS_URL}
                icon={<DataCollectionsIcon />}
                onClick={() => trackNavEvent('Data Collections')}
                subheader="Planet Data, Public Data, BYOC/ZARR"
              />

              <Resource
                title="Basemaps"
                href="/mosaics"
                icon={<MosaicsIcon />}
                component={RouteLink}
                onClick={() => trackNavEvent('Mosaics')}
                subheader="Basemaps API"
              />

              <Resource
                title="Destinations"
                href="/destinations"
                icon={<DestinationsIcon />}
                component={RouteLink}
                onClick={() => trackNavEvent('Destinations')}
                subheader="Destinations API"
              />
            </Section>
          </Stack>
        </Stack>

        <Divider
          flexItem
          orientation={mdDown ? 'horizontal' : 'vertical'}
          sx={{order: 2}}
        />

        <Stack sx={{flex: 1, gap: 2, order: mdDown ? 1 : 3}}>
          <Typography variant="overline2">
            Your data products
            {showDataProducts && products.length > 0
              ? ` (${products.length})`
              : ''}
          </Typography>

          {isLoading && <RotatingLoadingMessage messages={LOADING_MESSAGES} />}

          {error && (
            <Alert severity="error" variant="outlined">
              Error loading data products: {error.message}
            </Alert>
          )}

          {showDataProducts && products.length > 0 ? (
            <Stack
              aria-label="Data products"
              role="list"
              direction="row"
              sx={{flexWrap: 'wrap', gap: 1.5}}
            >
              {products.map((dataProduct, index) =>
                showAllProducts || index < NUM_DATA_PRODUCTS_TO_SHOW ? (
                  <ProductCard
                    key={`${dataProduct.productType}-${dataProduct.id}`}
                    title={dataProduct.title}
                    tooltipContent={dataProduct.description}
                    startDate={dataProduct.startDate}
                    endDate={dataProduct.endDate}
                    visible={
                      showAllProducts || index < NUM_DATA_PRODUCTS_TO_SHOW
                    }
                  />
                ) : null,
              )}
            </Stack>
          ) : (
            !isLoading &&
            !error && (
              <EmptyMessage
                icon={<EmptyStateIcon />}
                title="No products provisioned."
              />
            )
          )}

          {showDataProducts && products.length > NUM_DATA_PRODUCTS_TO_SHOW && (
            <div>
              <Button
                onClick={() => setShowAllProducts(state => !state)}
                variant="text"
              >
                {showAllProducts ? 'Collapse View' : 'View All'}
              </Button>
            </div>
          )}
        </Stack>
      </Stack>
    </PageWrapper>
  );
};

export default Overview;
